import Cookies from 'js-cookie';
import { handleSessionExpired } from './sessionGuard';

const PODUS_API_URL = import.meta.env.VITE_PODUS_API_URL ?? 'https://api-v2.podus.org';
const PODUS_SYSTEM_KEY = 'G@V3l2023-new-podus-v001233-SyS';

function getBase(): string {
    const trimmed = PODUS_API_URL.replace(/\/$/, '');
    return trimmed.endsWith('/api') ? trimmed : `${trimmed}/api`;
}

function buildHeaders(isMultipart = false): HeadersInit {
    const token = Cookies.get('p-ai-accessToken') ?? '';
    const headers: Record<string, string> = {
        Authorization: `Bearer ${PODUS_SYSTEM_KEY}${token ? `:${token}` : ''}`,
    };
    if (!isMultipart) headers['Content-Type'] = 'application/json';
    return headers;
}

async function handleJson<T>(res: Response): Promise<T> {
    const json = await res.json();
    if (!res.ok) {
        const message: string | undefined = json?.response?.message;
        // If the token is dead, kick over to /sign-in?redirect=… and stop here.
        if (handleSessionExpired(res.status, message)) {
            // Throw something so callers don't continue with an undefined value
            // while the redirect happens. The thrown error is never seen because
            // window.location.href is already in flight.
            throw new Error(message ?? 'Session expired');
        }
        throw new Error(message ?? 'Podus API request failed');
    }
    // NEW_PODUS_API wraps: { response: { data, message }, statusCode }
    return (json?.response?.data ?? json?.data ?? json) as T;
}

export const podusApi = {
    async get<T>(path: string): Promise<T> {
        const res = await fetch(`${getBase()}${path}`, { headers: buildHeaders() });
        return handleJson<T>(res);
    },
    async patch<T>(path: string, body?: unknown): Promise<T> {
        const res = await fetch(`${getBase()}${path}`, {
            method: 'PATCH',
            headers: buildHeaders(),
            body: body !== undefined ? JSON.stringify(body) : undefined,
        });
        return handleJson<T>(res);
    },
    async post<T>(path: string, body?: unknown): Promise<T> {
        const res = await fetch(`${getBase()}${path}`, {
            method: 'POST',
            headers: buildHeaders(),
            body: body !== undefined ? JSON.stringify(body) : undefined,
        });
        return handleJson<T>(res);
    },
};

export interface PodusUserProfile {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    country: string;
    state: string;
    address: string;
    accountType: string;
    profilePic: string;
}
